/**
 * What an entry costs and what it's worth — PLAN §3, steps 3 and 4.
 *
 * Both are pure functions of one {@link Entry} plus the filter context, which
 * is what makes the whole solver testable: every number the knapsack sees can
 * be checked by hand against the catalog row it came from.
 *
 * Two rules the rest of the solver leans on:
 *
 * - **Floats are rounded to {@link VALUE_PRECISION} decimals before anything
 *   compares them.** Not cosmetics: ties are load-bearing here (the tie-break
 *   is part of the spec), and two entries that "should" tie must actually
 *   compare equal rather than differ in the fifteenth bit. It also keeps the
 *   Swift port honest — same rounding, same ties, same plan.
 * - **`compareCandidates` is the one ordering.** Value desc, cost asc,
 *   orderIndex asc, exactly as PLAN §3 step 6 specifies, used for the knapsack
 *   item order, the greedy order and every emitted list.
 */

import type { Entry, RuntimeAlternative } from "@schema/catalog.ts";
import { type SolverWeights, UNLOCK_SCALE, UNRATED_QUALITY } from "./weights.ts";

/** Decimal places every emitted float is rounded to. */
export const VALUE_PRECISION = 4;

/**
 * Round half-up at `decimals`. Half-up rather than half-even because Swift's
 * `Double.rounded()` is half-away-from-zero and every number here is positive,
 * so the two agree — which is the whole point.
 */
export function roundTo(value: number, decimals: number = VALUE_PRECISION): number {
  if (!Number.isFinite(value)) return value;
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

/**
 * How you're going to cover this entry: end to end, or by one of the curated
 * shortcuts (PLAN §3, step 3 — `min(runtime.minutes, shortest alternative)`).
 */
export interface Route {
  /** What it costs you, in minutes. */
  minutes: number;
  /** The chosen shortcut, or `null` for the full runtime. */
  alternative: RuntimeAlternative | null;
  /** `alternative.valueRetention`, or 1 for the full runtime. */
  valueRetention: number;
  compressed: boolean;
}

/**
 * The cheapest way to cover `entry` when alternatives are enabled, the full
 * runtime when they aren't.
 *
 * Ties (two shortcuts of equal length) go to the one that retains more value,
 * then to the earlier one in the curated list — never to whichever the array
 * happened to sort into first. A shortcut that isn't actually shorter than the
 * full runtime is ignored, so a mis-authored alternative can't cost you value
 * for nothing.
 */
export function chooseRoute(entry: Entry, useAlternatives: boolean): Route {
  const full: Route = {
    minutes: entry.runtime.minutes,
    alternative: null,
    valueRetention: 1,
    compressed: false,
  };
  if (!useAlternatives) return full;

  let best: RuntimeAlternative | null = null;
  for (const alternative of entry.runtime.alternatives) {
    if (alternative.minutes >= entry.runtime.minutes) continue;
    if (best === null) {
      best = alternative;
      continue;
    }
    if (alternative.minutes < best.minutes) best = alternative;
    else if (
      alternative.minutes === best.minutes &&
      alternative.valueRetention > best.valueRetention
    ) {
      best = alternative;
    }
  }

  if (best === null) return full;
  return {
    minutes: best.minutes,
    alternative: best,
    valueRetention: best.valueRetention,
    compressed: true,
  };
}

/** Minutes this entry would give back on its shortest curated route; 0 if it has none. */
export function compressibleMinutes(entry: Entry): number {
  return Math.max(0, entry.runtime.minutes - chooseRoute(entry, true).minutes);
}

/**
 * How strongly an entry answers a facet you filtered on: Σ link weight over
 * the selected ids, saturating at 1.
 *
 * Saturating, not summing freely, because this term exists to say "yes, this
 * is one of the ones you asked for" — an entry that links three of your five
 * chosen characters shouldn't outrank an essential on the strength of that
 * alone. Empty selection means the facet is off, and contributes nothing.
 */
export function matchStrength(
  links: readonly { id: string; weight: number }[],
  selected: ReadonlySet<string>,
): number {
  if (selected.size === 0) return 0;
  let total = 0;
  for (const link of links) {
    if (selected.has(link.id)) total += link.weight;
  }
  return Math.min(1, Math.max(0, total));
}

/** The filter-dependent half of the value function, hoisted out of the per-entry loop. */
export interface ScoreContext {
  weights: SolverWeights;
  /** Selected character ids, already resolved against the catalog. */
  characters: ReadonlySet<string>;
  /** Selected thread ids, already resolved against the catalog. */
  threads: ReadonlySet<string>;
}

/**
 * Every term of the value function, kept separately. The solver only needs
 * `value`, but the advanced drawer needs to be able to show *why* an entry
 * ranks where it does, and a test that asserts one weighted term is far easier
 * to read than one that asserts the sum.
 */
export interface ValueBreakdown {
  tierValue: number;
  relevance: number;
  rosterMass: number;
  threadMass: number;
  characterMatch: number;
  threadMatch: number;
  quality: number;
  unlock: number;
  /** Σ of the eight terms, before compression. */
  subtotal: number;
  /** 1 unless the entry is being taken on a compressed route. */
  valueRetention: number;
  /** `subtotal × valueRetention`, rounded. This is what the knapsack maximises. */
  value: number;
}

/**
 * PLAN §3, step 4, term for term:
 *
 * ```
 * value = 1.00·tierValue + 0.35·relevance
 *       + 0.50·rosterMass + 0.40·threadMass
 *       + 0.80·selectedCharacterMatch + 0.80·selectedThreadMatch
 *       + 0.15·qualityScore + 0.20·(10 × unlockCount)
 * value *= alternative.valueRetention
 * ```
 *
 * The summation order is fixed and matches that listing — with floats, order
 * is part of the answer, and the Swift port has to add them up the same way.
 */
export function valueBreakdown(
  entry: Entry,
  context: ScoreContext,
  valueRetention: number,
): ValueBreakdown {
  const { weights } = context;
  const terms = {
    tierValue: weights.tierValue * entry.solver.tierValue,
    relevance: weights.relevance * entry.curation.relevance,
    rosterMass: weights.rosterMass * entry.solver.rosterMass,
    threadMass: weights.threadMass * entry.solver.threadMass,
    characterMatch:
      weights.characterMatch * matchStrength(entry.curation.characters, context.characters),
    threadMatch: weights.threadMatch * matchStrength(entry.curation.threads, context.threads),
    // An unrated entry scores the midpoint rather than zero. Zero would be an
    // active penalty for something nobody has judged — it would push every
    // unreleased film to the bottom of a plan that exists to get you ready for
    // exactly those. 50 is the neutral position: no help, no harm.
    quality: weights.quality * (entry.quality.score ?? UNRATED_QUALITY),
    unlock: weights.unlock * (UNLOCK_SCALE * entry.solver.unlockCount),
  };

  const subtotal =
    terms.tierValue +
    terms.relevance +
    terms.rosterMass +
    terms.threadMass +
    terms.characterMatch +
    terms.threadMatch +
    terms.quality +
    terms.unlock;

  return {
    tierValue: roundTo(terms.tierValue),
    relevance: roundTo(terms.relevance),
    rosterMass: roundTo(terms.rosterMass),
    threadMass: roundTo(terms.threadMass),
    characterMatch: roundTo(terms.characterMatch),
    threadMatch: roundTo(terms.threadMatch),
    quality: roundTo(terms.quality),
    unlock: roundTo(terms.unlock),
    subtotal: roundTo(subtotal),
    valueRetention,
    value: roundTo(subtotal * valueRetention),
  };
}

/** Just the number — `valueBreakdown(...).value`. */
export function entryValue(entry: Entry, context: ScoreContext, valueRetention: number): number {
  return valueBreakdown(entry, context, valueRetention).value;
}

/** The three fields every ordering in the solver sorts on. */
export interface Rankable {
  value: number;
  cost: number;
  orderIndex: number;
}

/**
 * The one canonical ordering — PLAN §3, step 6: **value desc, cost asc,
 * orderIndex asc**. `orderIndex` is unique across the catalog, so this is a
 * total order and every list the solver emits is reproducible.
 */
export function compareCandidates(a: Rankable, b: Rankable): number {
  if (a.value !== b.value) return b.value - a.value;
  if (a.cost !== b.cost) return a.cost - b.cost;
  return a.orderIndex - b.orderIndex;
}

/** Value per minute — the greedy fallback's ordering key. Free entries sort first. */
export function density(item: Rankable): number {
  return item.cost <= 0 ? Number.POSITIVE_INFINITY : item.value / item.cost;
}

/** Density desc, then {@link compareCandidates}. Used only by the greedy path. */
export function compareByDensity(a: Rankable, b: Rankable): number {
  const da = density(a);
  const db = density(b);
  if (da !== db) return db - da;
  return compareCandidates(a, b);
}
